import { Delete } from '@mui/icons-material'
import { Checkbox, FormControlLabel, Grid, IconButton } from '@mui/material'
import { observer } from 'mobx-react-lite'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import agent from '../../../api/agent'
import { getLocalizedRoleName } from '../../../common/roleHelper'
import { getErrMsg, toastNeg, toastPos } from '../../../common/toastHelper'
import { IRoleFormPackage } from '../../../interfaces/roles/IUserRoles'
import { ILinkedProject } from '../../../interfaces/skills/ISkills'
import { useStore } from '../../../Provider'
import '../profile.scss'
import ProjectRoleLink from './ProjectRoleLink'

interface OpenRoleProps {
  data?: IRoleFormPackage
}

const OpenRole = ({ data }: OpenRoleProps): JSX.Element => {
  const { t } = useTranslation()
  const { loginStore, personStore, projectStore } = useStore()
  const [linkedProjects, setLinkedProjects] = useState<ILinkedProject[]>(data?.linkedProjects || [])
  const { projects, getAllProjects } = projectStore

  // Load projects similar to skills implementation
  useEffect(() => {
    if (!projects || projects.length === 0) {
      void getAllProjects(loginStore.validatedUser)
    }
  }, [getAllProjects, loginStore.validatedUser, projects])

  // Initialize linked projects from role data if available
  useEffect(() => {
    if (!data || !projects) return

    let initialLinkedProjects = data.linkedProjects || data.role.linkedProjects || []

    // If we don't have linkedProjects but have projectNames, create linked projects from available project data
    if (initialLinkedProjects.length === 0 && data.role.projectNames && data.role.projectNames.length > 0) {
      initialLinkedProjects = data.role.projectNames
        .map((projectName) => {
          // Find the matching project from the projects store
          const matchingProject = projects.find((proj) => proj.project.name === projectName)

          if (matchingProject) {
            return {
              projectId: matchingProject.project.projectId,
              projectName: matchingProject.project.name,
              customer: matchingProject.project.customer || '',
              description: '',
              employerId: 0,
              endDate: '',
              kuukaudet: 0,
              manDays: 0,
              personId: loginStore.validatedUser,
              personName: '',
              projectOwner: 0,
              quota: 0,
              startDate: '',
              usingManDays: false,
            } as ILinkedProject
          }
          return null
        })
        .filter(Boolean) as ILinkedProject[]
    }

    setLinkedProjects(initialLinkedProjects)
  }, [data, projects, loginStore.validatedUser])

  if (!data) {
    return <></>
  }

  const { role } = data

  const handleRemoveProject = async (projectId: number) => {
    try {
      await personStore.deleteNewRoleProject(loginStore.validatedUser, role.roleId, projectId)
      const updatedLinks = linkedProjects.filter((p) => p.projectId !== projectId)
      setLinkedProjects(updatedLinks)
      toastPos(t('Toasts.RoleProjectRemove'))
    } catch (error) {
      console.error('Error removing role project link:', error)
      toastNeg(getErrMsg(error, t) || t('Toasts.RoleProjectRemoveError'))
    }
  }

  const handleKeyRoleChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const isKeyRole = event.target.checked

    try {
      await agent.MyRoles.updateUserRoleKeyStatus({
        PersonId: loginStore.validatedUser,
        RoleId: role.roleId,
        IsKeyRole: isKeyRole,
      })

      await personStore.getAllUserRoles(loginStore.validatedUser, false)
      role.isKeyRole = isKeyRole
      toastPos(t('Roles.KeyRoleUpdated'))
    } catch (error) {
      console.error('Error updating key role status:', error)
      toastNeg(t('Roles.KeyRoleUpdateError'))
    }
  }

  return (
    <Grid container className='gridcontainer--formcontainer__lastitempaddings'>
      <Grid item xs={12} className='griditem--formitem__titlemargin'>
        <span className='myskill--font__titlefont sini'>{getLocalizedRoleName(role)}</span>
      </Grid>

      <Grid container item xs={12} spacing={2}>
        <Grid item xs={12} md={6}>
          <Grid item xs={12}>
            <span className='myskill--font__titlefont subTitle'>{t('Roles.RoleName')} (FI)</span>
          </Grid>
          <Grid item xs={12}>
            <span className='myskill--font__personText'>{role.roleNameFi || t('Projects.NotDefined')}</span>
          </Grid>
        </Grid>

        <Grid item xs={12} md={6}>
          <Grid item xs={12}>
            <span className='myskill--font__titlefont subTitle'>{t('Roles.RoleName')} (EN)</span>
          </Grid>
          <Grid item xs={12}>
            <span className='myskill--font__personText'>{role.roleNameEng || t('Projects.NotDefined')}</span>
          </Grid>
        </Grid>

        <Grid item xs={12} md={6}>
          <Grid item xs={12}>
            <span className='myskill--font__titlefont subTitle'>{t('Roles.Category')} (FI)</span>
          </Grid>
          <Grid item xs={12}>
            <span className='myskill--font__personText'>{role.categoryFi || t('Projects.NotDefined')}</span>
          </Grid>
        </Grid>

        <Grid item xs={12} md={6}>
          <Grid item xs={12}>
            <span className='myskill--font__titlefont subTitle'>{t('Roles.Category')} (EN)</span>
          </Grid>
          <Grid item xs={12}>
            <span className='myskill--font__personText'>{role.categoryEng || t('Projects.NotDefined')}</span>
          </Grid>
        </Grid>

        <Grid item xs={12} md={6}>
          <Grid item xs={12}>
            <span className='myskill--font__titlefont subTitle'>{t('Skills.Expirience')}</span>
          </Grid>
          <Grid item xs={12}>
            <span className='myskill--font__personText'>
              {role.experienceDays || 0} {t('Date.ExperienceDays')}
            </span>
          </Grid>
        </Grid>

        <Grid item xs={12} className='griditem--formitem__formtoppadding'>
          <FormControlLabel
            control={<Checkbox checked={role.isKeyRole || false} onChange={handleKeyRoleChange} color='primary' />}
            label={t('Roles.MarkAsKeyRole')}
            className='myskill--font__personText'
          />
        </Grid>

        {linkedProjects.length > 0 && (
          <Grid item xs={12}>
            <Grid item xs={12}>
              <span className='myskill--font__titlefont subTitle'>{t('Roles.LinkedProjects')}</span>
            </Grid>
            <Grid item xs={12}>
              {linkedProjects.map((project) => (
                <Grid
                  container
                  key={project.projectId}
                  item
                  className='gridcontainer--addskilltoproject__skillcontainer'
                  wrap='nowrap'
                  justifyContent='space-between'
                >
                  <Grid item className='gridcontainer--addskilltoproject__hideoverflow'>
                    <span className='myskill--font__personText'>
                      {project.projectName}
                      {project.customer && ` (${project.customer})`}
                    </span>
                  </Grid>
                  <Grid item>
                    <IconButton size='small' onClick={() => handleRemoveProject(project.projectId)} aria-label={t('Skills.RemoveProject')}>
                      <Delete fontSize='small' />
                    </IconButton>
                  </Grid>
                </Grid>
              ))}
            </Grid>
          </Grid>
        )}

        <Grid item xs={12} className='griditem--formitem__formtoppadding'>
          <ProjectRoleLink
            setLinkingProject={() => undefined} // No longer needed since it's always shown
            setLinkedProjects={setLinkedProjects}
            editCurrentRole={role}
            linkedProjects={linkedProjects}
          />
        </Grid>
      </Grid>
    </Grid>
  )
}

export default observer(OpenRole)
